import { useId, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/** A titled group of related settings. */
export function RuleGroup({
  icon: Icon,
  title,
  description,
  children,
  columns = 2,
  className,
}: {
  icon: LucideIcon;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  /**
   * "2": two field columns from the sm breakpoint.
   * "stacked": groups shown side by side from xl, so their fields stack there.
   */
  columns?: 2 | "stacked";
  className?: string;
}) {
  const titleId = useId();
  return (
    <fieldset
      aria-labelledby={titleId}
      className={cn(
        "rounded-xl border border-gray-200 bg-gray-50/60 p-5 dark:border-gray-700 dark:bg-gray-900/30",
        className
      )}
    >
      <div className="mb-4 flex items-start gap-3">
        <span className="bg-primary/10 text-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        <div>
          <legend id={titleId} className="text-sm font-semibold text-gray-900 dark:text-white">
            {title}
          </legend>
          {description && (
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{description}</p>
          )}
        </div>
      </div>
      <div className={cn("grid gap-4 sm:grid-cols-2", columns === "stacked" && "xl:grid-cols-1")}>
        {children}
      </div>
    </fieldset>
  );
}

/** Number or amount input with its unit shown inside the field. */
export function UnitField({
  label,
  value,
  onChange,
  unit,
  hint,
  error,
  inputMode = "numeric",
  placeholder,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  unit: string;
  hint?: string | undefined;
  error?: string | undefined;
  inputMode?: "numeric" | "decimal";
  placeholder?: string | undefined;
  disabled?: boolean;
}) {
  const id = useId();
  const describedBy = [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(" ");

  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm text-gray-700 dark:text-gray-200">
        {label}
      </label>
      <div
        className={cn(
          "focus-within:ring-ring flex items-center rounded-lg border bg-white transition-shadow focus-within:ring-2 dark:bg-gray-800",
          error ? "border-red-400 dark:border-red-500" : "border-gray-300 dark:border-gray-600",
          disabled && "opacity-50"
        )}
      >
        <input
          id={id}
          type="text"
          inputMode={inputMode}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          className="w-full min-w-0 rounded-lg bg-transparent px-3 py-2 text-sm text-gray-900 tabular-nums outline-none focus-visible:ring-0 focus-visible:ring-offset-0 dark:text-white"
        />
        <span className="pr-3 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
          {unit}
        </span>
      </div>
      {error ? (
        <p id={`${id}-error`} className="mt-1 text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      ) : (
        hint && (
          <p id={`${id}-hint`} className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {hint}
          </p>
        )
      )}
    </div>
  );
}

/** On/off setting with a sentence explaining what "on" means. */
export function SwitchField({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <p id={`${id}-label`} className="text-sm text-gray-900 dark:text-white">
          {label}
        </p>
        {description && (
          <p id={`${id}-description`} className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {description}
          </p>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={`${id}-label`}
        aria-describedby={description ? `${id}-description` : undefined}
        onClick={() => {
          onChange(!checked);
        }}
        className={cn(
          "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
          checked ? "bg-primary" : "bg-gray-300 dark:bg-gray-600"
        )}
      >
        <span
          className={cn(
            "inline-block h-5 w-5 rounded-full bg-white shadow transition-transform",
            checked ? "translate-x-5" : "translate-x-0.5"
          )}
        />
      </button>
    </div>
  );
}

const SELECT_CLASSES =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white";

export function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm text-gray-700 dark:text-gray-200">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className={SELECT_CLASSES}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Single-line text input with its label, and either an error or a hint below. */
export function TextField({
  label,
  value,
  onChange,
  error,
  hint,
  placeholder,
  maxLength,
  autoFocus,
  mono,
  type = "text",
  autoComplete = "off",
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | undefined;
  hint?: string;
  placeholder?: string;
  maxLength: number;
  autoFocus?: boolean;
  mono?: boolean;
  type?: "text" | "email" | "password" | "tel";
  autoComplete?: string;
  required?: boolean;
}) {
  const id = useId();
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;

  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm text-gray-700 dark:text-gray-200">
        {label}
      </label>
      <input
        id={id}
        type={type}
        required={required}
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        spellCheck={!mono}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className={cn(
          "w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-900 dark:bg-gray-800 dark:text-white",
          mono && "font-mono tracking-wider",
          error ? "border-red-400 dark:border-red-500" : "border-gray-300 dark:border-gray-600"
        )}
      />
      {error ? (
        <p id={`${id}-error`} className="mt-1 text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      ) : (
        hint && (
          <p id={`${id}-hint`} className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {hint}
          </p>
        )
      )}
    </div>
  );
}
